import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Renders a static map preview + distance/duration card for a pickup → dropoff route.
 * Lazily fetches the distance matrix when both addresses are set, debounced to
 * avoid hammering the Maps API as the user types.
 *
 * Props:
 *  - pickupAddress: string
 *  - dropoffAddress: string
 */
const RoutePreview = ({ pickupAddress, dropoffAddress }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mapVisible, setMapVisible] = useState(true);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    let cancelled = false;

    if (!pickupAddress || !dropoffAddress) {
      const id = window.setTimeout(() => {
        if (cancelled) return;
        setInfo(null);
        setError('');
      }, 0);
      return () => {
        cancelled = true;
        window.clearTimeout(id);
      };
    }

    debounceTimer.current = setTimeout(async () => {
      if (cancelled) return;
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${API}/maps/distance-matrix`, {
          params: { origin: pickupAddress, destination: dropoffAddress },
        });
        if (cancelled) return;
        setInfo(response.data);
        setMapVisible(true);
      } catch (err) {
        if (cancelled) return;
        setInfo(null);
        setError(
          err.response?.data?.detail ||
            'Could not compute the route. Please check the addresses.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 700);

    return () => {
      cancelled = true;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [pickupAddress, dropoffAddress]);

  if (!pickupAddress || !dropoffAddress) return null;

  // Different aspect ratios per breakpoint so the route is comfortably visible
  // on both mobile (4:3) and desktop (16:9-ish).
  const mapSrc =
    info && mapVisible
      ? `${API}/maps/static-map?origin=${encodeURIComponent(
          pickupAddress
        )}&destination=${encodeURIComponent(dropoffAddress)}&width=640&height=360`
      : null;

  return (
    <div
      className="mb-6 bg-black/30 border border-white/10 rounded-2xl overflow-hidden"
      data-testid="route-preview"
    >
      {loading && (
        <div className="flex items-center justify-center py-10 gap-3">
          <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
          <span className="text-gray-400 text-sm">Calculating route...</span>
        </div>
      )}

      {!loading && error && (
        <div className="px-5 py-4 text-amber-200/70 text-sm flex items-start gap-3">
          <MapPin className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && info && (
        <>
          {mapSrc && (
            <div className="w-full aspect-[16/9] bg-[#0a0a0a] relative flex items-center justify-center">
              <img
                src={mapSrc}
                alt="Route preview"
                className="w-full h-full object-contain"
                onError={() => setMapVisible(false)}
                data-testid="route-preview-map"
              />
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4 p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Navigation className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">
                  Distance
                </p>
                <p
                  className="text-white text-xl font-semibold"
                  data-testid="route-distance"
                >
                  {info.distance_text}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-teal-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">
                  Estimated duration
                </p>
                <p
                  className="text-white text-xl font-semibold"
                  data-testid="route-duration"
                >
                  {info.duration_text}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RoutePreview;
